"use client";

import type { LcIssuance } from "@/lib/sse/useSSE";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * LcAdvisedToYou — Aurora's exporter-side view of T2 4.13 LC issuance.
 *
 * Bridges the asymmetry where the importer-natural T2 LC issuance moment
 * had Aurora staring at the generic `TradeInProgressExporter` placeholder.
 * Now her view frames the moment from the beneficiary's perspective:
 *
 *   1. The LC has been ADVISED to her (MT 710) by the seller-side
 *      advising bank — she hasn't issued anything; the buyer's bank has.
 *   2. She has a presentation window to produce the documents listed.
 *   3. Documents → docs presented → MT 754 advice of payment → settlement.
 *
 * Pure presentational: takes the LC issuance projection, renders the
 * beneficiary framing. No state, no fetches.
 */
export function LcAdvisedToYou({
  lc,
  advisingBankName = "Banco do Brasil",
  presentationPeriodDays = 21,
}: {
  lc: LcIssuance | null;
  advisingBankName?: string;
  presentationPeriodDays?: number;
}) {
  const t = useTranslations("lcAdvisedToYou");

  if (!lc) {
    return (
      <div className="grid h-full place-items-center">
        <p className="font-mono text-[11px] text-orbitra-muted">
          {t("awaitingAdvice")}
        </p>
      </div>
    );
  }

  const issuingBank = lc.buyerSideBankBadge ?? "FAB";
  const fundingPct = lc.fundingMix?.bankFinancingPct ?? 30;

  // Standard document checklist the beneficiary must present under
  // UCP 600 to draw on the LC. Keep this list concrete — the audience
  // should read it as "real list of paper that Aurora has 21 days to
  // collect," not abstract.
  const documents: { name: string; status: "ready" | "pending" }[] = [
    { name: t("doc1Name"), status: "ready"   },
    { name: t("doc2Name"), status: "ready"   },
    { name: t("doc3Name"), status: "ready"   },
    { name: t("doc4Name"), status: "ready"   },
    { name: t("doc5Name"), status: "ready"   },
    { name: t("doc6Name"), status: "ready"   },
    { name: t("doc7Name"), status: "pending" },
  ];

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="glass p-5">
        <div className="flex items-baseline justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
              {t("kickerBeneficiary")}
            </p>
            <h2 className="mt-1 font-display text-xl font-semibold text-orbitra-paper">{lc.lcReference}</h2>
            <p className="mt-1 text-[12px] text-orbitra-teal-soft">
              {t("amountExpiry", { amount: lc.amountUsd.toLocaleString(), expiry: lc.expiry })}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5 text-[10px]">
            <span className="rounded-full border border-state-financed/40 bg-state-financed/8 px-3 py-1 font-mono uppercase tracking-wider text-state-financed">
              {t("advisedVia", { advisingBankName })}
            </span>
            <span className="font-mono text-orbitra-teal-soft">
              {t("issuedBy", { issuingBank, fundingPct })}
            </span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
          <div className="rounded-md border border-state-financed/30 bg-orbitra-ink/40 px-3 py-2.5">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("swiftChainKicker")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>MT 700 from {issuingBank} → <span className="text-state-financed">received by {advisingBankName}</span> ✓</li>
              <li>MT 710 from {advisingBankName} → <span className="text-state-financed">{t("swiftReceivedByYou")}</span> ✓</li>
              <li>{t("swiftAdviceOfPayment")} <span className="font-mono text-orbitra-muted">{t("swiftFiresWhenDocsAccepted")}</span></li>
              <li>{t("swiftAdviceOfReimbursement")} <span className="font-mono text-orbitra-muted">{t("swiftFiresAtSettlement")}</span></li>
            </ul>
          </div>

          <div className="rounded-md border border-state-traced/30 bg-orbitra-ink/40 px-3 py-2.5">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("presentationWindowKicker")}
            </p>
            <p className="mt-2 font-display text-2xl font-semibold text-state-traced">
              {presentationPeriodDays} days
            </p>
            <p className="mt-1 text-[10px] text-orbitra-muted">
              {t("presentationWindowBody")}
            </p>
            <p className="mt-2 text-[10px] text-state-traced">
              {t("daysElapsed", { days: presentationPeriodDays })}
            </p>
          </div>
        </div>
      </div>

      <div className="glass p-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
          {t("documentsKicker")}
        </p>
        <ul className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
          {documents.map((d) => (
            <li
              key={d.name}
              className={
                d.status === "ready"
                  ? "rounded border border-state-traced/40 bg-state-traced/8 px-3 py-1.5 text-state-traced"
                  : "rounded border border-state-future/40 bg-state-future/8 px-3 py-1.5 text-state-future"
              }
            >
              <span className="font-mono text-[9px] uppercase tracking-wider">
                {d.status === "ready" ? t("docStatusReady") : t("docStatusPending")}
              </span>
              <p className="mt-0.5 text-orbitra-paper">{d.name}</p>
            </li>
          ))}
        </ul>

        <p className="mt-4 text-[10px] text-orbitra-muted">
          {t("footerText", { advisingBankName, issuingBank })}
        </p>
      </div>
    </div>
  );
}
